import { create } from 'zustand'

export type UsahaKey = 'TMC' | 'SDP'

export type TabType = {
  id: string
  label: string
  modul: string
}

type UIState = {
  selectedUsaha: UsahaKey
  tabsByUsaha: Record<UsahaKey, TabType[]>
  currentTabByUsaha: Record<UsahaKey, number>
  setSelectedUsaha: (usaha: UsahaKey) => void
  setActiveCurrentTab: (index: number) => void
  addOrFocusTab: (modul: string, label: string) => void
  removeActiveTab: (index: number) => void
  reorderActiveTabs: (from: number, to: number) => void
  hydrateFromStorage: () => void
}

export const useUIStore = create<UIState>((set, get) => ({
  selectedUsaha: 'TMC',
  tabsByUsaha: { TMC: [], SDP: [] },
  currentTabByUsaha: { TMC: 0, SDP: 0 },

  setSelectedUsaha: usaha => {
    set({ selectedUsaha: usaha })

    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedUsaha', usaha)
    }
  },

  setActiveCurrentTab: index => {
    const usaha = get().selectedUsaha

    set({ currentTabByUsaha: { ...get().currentTabByUsaha, [usaha]: index } })
  },

  addOrFocusTab: (modul, label) => {
    const usaha = get().selectedUsaha
    const tabs = get().tabsByUsaha[usaha] ?? []
    const idx = tabs.findIndex(t => t.modul === modul && t.label === label)

    let newTabs = tabs
    let newIndex = get().currentTabByUsaha[usaha] ?? 0

    if (idx >= 0) {
      newIndex = idx
    } else {
      const newTab: TabType = {
        id: `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        modul,
        label
      }

      newTabs = [...tabs, newTab]
      newIndex = newTabs.length - 1
    }

    set({
      tabsByUsaha: { ...get().tabsByUsaha, [usaha]: newTabs },
      currentTabByUsaha: { ...get().currentTabByUsaha, [usaha]: newIndex }
    })

    if (typeof window !== 'undefined') {
      localStorage.setItem(`menuTabs:${usaha}`, JSON.stringify(newTabs))
    }
  },

  removeActiveTab: index => {
    const usaha = get().selectedUsaha
    const tabs = get().tabsByUsaha[usaha] ?? []
    const newTabs = tabs.filter((_, i) => i !== index)
    let current = get().currentTabByUsaha[usaha] ?? 0

    if (current >= index) {
      current = Math.max(0, current - 1)
    }

    set({
      tabsByUsaha: { ...get().tabsByUsaha, [usaha]: newTabs },
      currentTabByUsaha: { ...get().currentTabByUsaha, [usaha]: current }
    })

    if (typeof window !== 'undefined') {
      localStorage.setItem(`menuTabs:${usaha}`, JSON.stringify(newTabs))
    }
  },

  reorderActiveTabs: (from, to) => {
    const usaha = get().selectedUsaha
    const tabs = [...(get().tabsByUsaha[usaha] ?? [])]
    const draggedItem = tabs[from]

    tabs.splice(from, 1)
    tabs.splice(to, 0, draggedItem)

    let current = get().currentTabByUsaha[usaha] ?? 0

    if (current === from) {
      current = to
    } else if (current >= to && current < from) {
      current = current + 1
    } else if (current <= to && current > from) {
      current = current - 1
    }

    set({
      tabsByUsaha: { ...get().tabsByUsaha, [usaha]: tabs },
      currentTabByUsaha: { ...get().currentTabByUsaha, [usaha]: current }
    })

    if (typeof window !== 'undefined') {
      localStorage.setItem(`menuTabs:${usaha}`, JSON.stringify(tabs))
    }
  },

  hydrateFromStorage: () => {
    if (typeof window === 'undefined') return

    const saved = localStorage.getItem('selectedUsaha') as UsahaKey | null
    const usaha = saved === 'SDP' ? 'SDP' : 'TMC'

    const readTabs = (u: UsahaKey) => {
      const key = `menuTabs:${u}`
      const namespaced = localStorage.getItem(key)

      if (namespaced && namespaced !== '[]') {
        return JSON.parse(namespaced) as TabType[]
      }

      const legacy = localStorage.getItem('menuTabs')

      if (legacy && legacy !== '[]') {
        localStorage.setItem(key, legacy)

        return JSON.parse(legacy) as TabType[]
      }

      return []
    }

    set({
      selectedUsaha: usaha,
      tabsByUsaha: { TMC: readTabs('TMC'), SDP: readTabs('SDP') }
    })
  }
}))
